export const business = {
  name: "Karalewich",
  descriptor: "Custom Software & AI Solutions",
  email: process.env.REACT_APP_CONTACT_EMAIL || "vasili@karalewich.com",
  publicEmail: "hello@karalewich.com",
  phone: "201-500-5343",
  phoneHref: "+12015005343",
  location: "New Jersey, working everywhere.",
  founder: "Vasili Karalewich",
};

export const services = [
  {
    id: "custom-software",
    number: "01",
    short: "Custom software",
    icon: "PanelsTopLeft",
    title: "Built around your business.",
    summary:
      "Turn quoting sheets, PDF forms, and everyday company processes into connected software that saves your team time.",
    headline: "Your processes. Built into software.",
    intro:
      "When a spreadsheet, a patchwork of tools, or an off-the-shelf system starts holding you back, it may be time for software that fits.",
    examples: [
      "Quoting & performance tools",
      "PDFs turned into custom forms",
      "Connected business processes",
    ],
    details: [
      [
        "See the whole picture",
        "Bring information from the systems you already use into an interface your team can actually use.",
      ],
      [
        "Simplify the everyday",
        "Replace repetitive handoffs with clear workflows, useful notifications, and the right information at the right time.",
      ],
      [
        "Make room to grow",
        "Start with a focused solution, then expand it as your business and your priorities develop.",
      ],
    ],
  },
  {
    id: "ai-solutions",
    number: "02",
    short: "AI & automation",
    icon: "Sparkles",
    title: "Your knowledge. Put to work.",
    summary:
      "Build AI agents and assistants around your company knowledge, for internal questions, routine work, and customer-facing support.",
    headline: "Bring useful intelligence into everyday work.",
    intro:
      "Connect the company information you approve to AI assistants that can answer questions, prepare work, and support the right actions for your team or customers.",
    examples: [
      "Internal AI agents & assistants",
      "Customer-facing AI assistants",
      "Document & workflow automation",
    ],
    details: [
      [
        "Answers with context",
        "Help people find information across documents and processes, with sources they can review.",
      ],
      [
        "Less repetitive work",
        "Organize information, prepare drafts, and connect routine steps while keeping people in control.",
      ],
      [
        "A considered approach",
        "Define the data, permissions, and review steps around the task. Evaluate the experience before expanding it.",
      ],
    ],
  },
  {
    id: "web-apps",
    number: "03",
    short: "Web apps & portals",
    icon: "AppWindow",
    title: "An experience worth opening.",
    summary:
      "Build websites and portals that use your company data, support real tasks, and connect customers to the right information.",
    headline: "Turn a useful idea into a great application.",
    intro:
      "Go beyond static pages. Connect product information, quoting, customer records, custom forms, and AI assistance where your business needs them.",
    examples: [
      "Data-driven customer websites",
      "Customer & partner portals",
      "Connected business applications",
    ],
    details: [
      [
        "Make it feel effortless",
        "Clear interfaces, considered interactions, and responsive layouts that help people move through the task.",
      ],
      [
        "Connect the whole journey",
        "Bring accounts, files, requests, payments, and your existing systems together where your project needs them.",
      ],
      [
        "Build beyond launch",
        "Keep improving the product with feedback, useful analytics, and an ongoing development partnership.",
      ],
    ],
  },
];

// Prior-company case studies are retired from public presentation.
export const projects = [];

export const processSteps = [
  {
    number: "01",
    title: "Understand the work.",
    text: "Start with meetings and questions about your business. Review the documents, forms, spreadsheets, and systems your team works with.",
  },
  {
    number: "02",
    title: "Turn it into software.",
    text: "Build quoting tools, digital forms, performance views, and connected workflows around the way your business operates.",
  },
  {
    number: "03",
    title: "Keep improving together.",
    text: "Continue your partnership with Karalewich as priorities evolve. Add integrations, AI assistance, and improvements where they make the work easier.",
  },
];

export const questions = [
  [
    "Where do we start?",
    "With a short discovery conversation about your established business and how the work happens today. You do not need to know what software to ask for. Vasili helps identify opportunities and, when relevant, can privately screen-share a permitted application to make the possibilities clearer.",
  ],
  [
    "Can you work with our existing systems?",
    "Yes. Integrations and improvements to existing systems can be part of the project. We first review what you use, what access is available, and where a connection will create the most value.",
  ],
  [
    "Do you offer ongoing development?",
    "Yes. Ongoing development partnerships are the main way of working. We work with you to set priorities, build useful tools, and improve them as the business changes. A focused initial project can be the starting point.",
  ],
  [
    "What does a project cost?",
    "Pricing follows the scope, complexity, and ongoing support required. After discussing the project, Vasili can outline the scope, an approach, and the investment required.",
  ],
  [
    "Do you still build websites?",
    "Yes. Modern websites, analytics, and supporting digital services remain available. The main focus is now the software, applications, and AI solutions that help a business operate and grow.",
  ],
];

export const softwareQuestions = [
  [
    "Can you replace our Excel spreadsheets with custom software?",
    "Yes. Start with the spreadsheets and processes your team already uses. We can turn customer records, quoting sheets, approval steps, and reporting into a shared web application with a database and access permissions.",
  ],
  [
    "Can our PDF forms become digital forms?",
    "Yes. Forms can be rebuilt as web-based workflows that check required information, store records in a database, and route completed work for review. Existing PDFs and the way your team uses them are the starting point.",
  ],
  [
    "Can you turn a static website into a database-driven website?",
    "Yes. A project can connect product information, searchable catalogs, filters, customer portals, and inquiry tools to company data. We review your current website and systems before deciding what to keep or rebuild.",
  ],
  [
    "Do you offer ongoing software development?",
    "Yes. Ongoing partnerships are the preferred way of working. We agree priorities together and improve the software as your established business evolves.",
  ],
  [
    "Do we need a detailed specification before contacting you?",
    "No. Start with how the work happens today and what slows people down. A discovery conversation helps identify opportunities. A relevant private screen-share can help make the possibilities easier to picture.",
  ],
  [
    "Where is Karalewich based?",
    "Karalewich is based in New Jersey and works remotely with businesses elsewhere. Our work is led by founder Vasili Karalewich.",
  ],
];
export const aiQuestions = [
  [
    "Can an AI assistant answer questions using our company documents?",
    "An assistant can be connected to approved documents, policies, and product information. The project defines its sources, access permissions, source references, and what happens when an answer is unavailable.",
  ],
  [
    "Can you add an AI assistant to our customer-facing website?",
    "Yes. An assistant can help customers find relevant product or service information using the company material you approve. We define its scope and a route to a person when needed.",
  ],
  [
    "What business workflows can be automated?",
    "Examples include extracting information from forms, preparing records or drafts, routing requests, and connecting steps between existing systems. We first review the process, available integrations, and approval requirements.",
  ],
  [
    "Does every automation need AI?",
    "No. A reliable integration or a clear software rule may be the better choice for a predictable task. AI is considered where interpreting documents, working with language, or finding relevant information helps.",
  ],
  [
    "Can we start with one process?",
    "Yes. A focused initial task lets us evaluate usefulness, check responses and permissions, and improve the approach with your team before expanding it.",
  ],
];

// Original public profiles; BBB accreditation/rating verified November 4, 2026.
export const bbbProfileURL = "https://www.bbb.org/us/nj/river-vale/profile/app-developers/karalewich-llc-0221-90236276";
export const businessProfiles = [
  ["LinkedIn", "https://www.linkedin.com/company/karalewich/"],
  ["Instagram", "https://www.instagram.com/karalewich"],
  ["Facebook", "https://www.facebook.com/profile.php?id=61566329611249"],
  ["YouTube", "https://www.youtube.com/@Karalewich"],
];
